import { fromJS, List, Map } from 'immutable';
import { dispatch } from 'nike-re-framejs';

import forumClient from '@/_http/axiosForumClient';
import PopupNg from '@/ui/PopupNg';
import i18n from '@i18n';
import { timeFilterIntervals } from '@/betbook/TimeFilterIntervals';
import { COMMUNITY_POSTS_AUTHOR, CHAT_NEW_MESSAGE_WITH_PARAMS_ID } from '@/routing/routes';
import { navigate } from '@/routing';
import { stats2Client } from 'nike-ui/src/common/axios/stats2Client';
import { BETTABLE, TIME_3HOURS, toBetbookAuthorPathParams } from 'nike-ui/src/components/betbook/constants';
import { toUrlPart } from 'nike-ui/src/components/betbook/handlers';
import { betbookListRoute } from '@/betbook/utils';
import { translateServerError } from 'nike-ui/src/components/betbook/utils/translateServerError';
import { loadBoxByBetIds as n1LoadBoxByBetIds } from '@/nikeone/services';
import { mergeLoadedBoxes } from '@/nikeone/handlers';
import { fetchMinimalTickets, fetchTicket } from '@/mytickets/detail/api';
import { fetchMarketStatistics } from 'nike-ui/src/components/homepage/handlers';

const PAGE_SIZE = 10;

const basicModelModelShapeObject = {
    byId: {},
    allIds: []
};

const normalizeComments = comments => comments.reduce(commentsNormalisation, fromJS(basicModelModelShapeObject));

const commentsNormalisation = (acc, item) => {
    const itemChildren = item.children;
    const newAcc = acc
        .setIn(['byId', item.id], fromJS(item))
        .setIn(['byId', item.id, 'children'], List())
        .update(db =>
            item.parentCommentId
                ? db.updateIn(['byId', item.parentCommentId, 'children'], old => old.push(item.id))
                : db.updateIn(['allIds'], old => old.concat(item.id))
        );
    if (itemChildren?.length > 0) {
        return itemChildren.reduce(commentsNormalisation, newAcc);
    }
    return newAcc;
};

const setCreateAnalysisBetIds = betIds => db => db.setIn(['betbook', 'create-analysis-betIds'], betIds);

export const loadCreateAnalysisBetIds = ticketId => () => {
    forumClient
        .get(`/v5/betbook/gaming-opportunities/bet-ids?ticketId=${ticketId}`)
        .then(betIds => betIds && dispatch(setCreateAnalysisBetIds(betIds)));
};

function createParams(
    sorting,
    offset = 0,
    count = PAGE_SIZE,
    expirationTag,
    sportIds,
    odds,
    stake,
    matchId,
    win,
    betCount
) {
    return {
        betCountFrom: betCount?.[0],
        betCountTo: betCount?.[1],
        bettable: expirationTag === BETTABLE || undefined,
        count,
        expirationFrom: expirationTag === BETTABLE ? undefined : timeFilterIntervals[expirationTag]?.expirationFrom(),
        expirationTo: expirationTag === BETTABLE ? undefined : timeFilterIntervals[expirationTag]?.expirationTo(),
        matchId,
        oddsFrom: odds?.[0],
        oddsTo: odds?.[1],
        offset,
        sorting,
        sportIds: sportIds?.join(',') || undefined,
        stakeFrom: stake?.[0],
        stakeTo: stake?.[1],
        winFrom: win?.[0],
        winTo: win?.[1]
    };
}

export const loadBetbookPosts = (
    type,
    sorting,
    sportIds,
    odds,
    stake,
    win,
    betCount,
    expirationTag,
    matchId,
    offset = 0,
    count = PAGE_SIZE
) => db => {
    matchId = matchId || undefined;
    let params;
    let filter;
    if (type === 'ANALYSIS') {
        params = createParams(sorting, offset, count, expirationTag, sportIds, odds, stake, matchId);
        filter = fromJS({ type, count, expirationTag, matchId, odds, offset, sorting, sportIds, stake });
    } else if (type === 'BLOG') {
        params = createParams(sorting, offset, count, expirationTag);
        filter = fromJS({ type, count, expirationTag, offset, sorting });
    } else {
        params = createParams(sorting, offset, count, expirationTag, sportIds, odds, stake, undefined, win, betCount);
        filter = fromJS({ type, betCount, count, expirationTag, odds, offset, sorting, sportIds, stake, win });
    }
    forumClient
        .get(`/v5/betbook/${toUrlPart(type)}`, { params })
        .catch(() => [])
        .then(posts => {
            dispatch(postsLoaded(posts, filter));
            if (posts?.length) {
                if (type === 'ANALYSIS') {
                    const betIds = betIdsToLoad(db, posts);
                    if (betIds.length) {
                        n1LoadBoxByBetIds(betIds).then(bets => {
                            dispatch(mergeLoadedBoxes({ bets }, 'betbook', betIds));
                        });
                    }
                    fetchMarketStatistics(posts.map(p => p.pick.matchId));
                } else if (type === 'TICKET') {
                    const hashes = posts.map(post => post.ticket.hash);
                    fetchMinimalTickets(hashes);
                }
            }
        });
    return db.setIn(['betbook', 'posts', 'filter'], filter);
};

function betIdsToLoad(db, posts) {
    const existingBetIds = db
        .getIn(['nikeone', 'boxstore', 'betbook', 'bets'], Map())
        .keySeq()
        .toJS();
    // skip duplicates
    const betIds = [...new Set(posts.map(post => post.pick.betId))];
    // skip already loaded
    return betIds.filter(betId => !existingBetIds.includes(String(betId)));
}

export const loadAuthorBetbookPosts = (nickname, type, offset, sorting) => db => {
    const params = { count: PAGE_SIZE, offset, sorting };
    const filter = fromJS({ count: PAGE_SIZE, nickname, offset, sorting, type });
    forumClient
        .get(`/v5/betbook/authors/${nickname}/${toUrlPart(type)}`, { params })
        .catch(() => [])
        .then(posts => {
            dispatch(postsLoaded(posts, filter));
            if (posts?.length) {
                if (type === 'ANALYSIS') {
                    const betIds = betIdsToLoad(db, posts);
                    if (betIds.length) {
                        n1LoadBoxByBetIds(betIds).then(bets => {
                            dispatch(mergeLoadedBoxes({ bets }, 'betbook', betIds));
                        });
                    }
                    fetchMarketStatistics(posts.map(p => p.pick.matchId));
                } else if (type === 'TICKET') {
                    const hashes = posts.map(post => post.ticket.hash);
                    fetchMinimalTickets(hashes);
                }
            }
        });
    return db.setIn(['betbook', 'posts', 'filter'], filter);
};

function postsLoaded(posts, loadFilter) {
    return db => {
        const hasMore = loadFilter.get('count') === posts.length;
        const firstPage = loadFilter.getIn(['offset'], 0) === 0;
        // remove already loaded posts due to the shift when the new analysis has been created
        const loaded = db.getIn(['betbook', 'posts', 'list'], List()).toJS();
        const newPosts = posts.filter(p => loaded.every(l => l.id !== p.id));
        return db
            .updateIn(['betbook', 'posts', 'list'], list => (firstPage ? fromJS(posts) : list.concat(fromJS(newPosts))))
            .setIn(['betbook', 'posts', 'hasMore'], hasMore)
            .setIn(['betbook', 'posts', 'filter-loaded'], loadFilter);
    };
}

export const loadMoreBetbookPosts = postType => db => {
    const filter = db.getIn(['betbook', 'posts', 'filter']).toJS();
    const { betCount, expirationTag, matchId, odds, offset: off, sorting, sportIds, stake, win } = filter;
    const offset = off + PAGE_SIZE;
    dispatch(loadBetbookPosts(postType, sorting, sportIds, odds, stake, win, betCount, expirationTag, matchId, offset));
    return db;
};

export const loadMoreAuthorBetbookPosts = postType => db => {
    const { nickname, offset, sorting } = db.getIn(['betbook', 'posts', 'filter']).toJS();
    dispatch(loadAuthorBetbookPosts(nickname, postType, offset + PAGE_SIZE, sorting));
    return db;
};

export const deletePost = (postType, postId, lastPageParams) => () => {
    forumClient
        .delete(`/v5/betbook/${toUrlPart(postType)}/${postId}`)
        .then(() => {
            dispatch(deletePostFromDB(postId));
            PopupNg.dispatcher.close('betbook/postOptions');
            if (lastPageParams) {
                if (lastPageParams.nickname) {
                    navigate(COMMUNITY_POSTS_AUTHOR, toBetbookAuthorPathParams(lastPageParams.nickname));
                } else {
                    navigate(betbookListRoute(postType));
                }
            } else {
                navigate(betbookListRoute(postType));
            }
        })
        .catch(e => {
            openErrorMessageModal(e.response);
        });
};

const deletePostFromDB = postId => db =>
    db.updateIn(['betbook', 'posts', 'list'], list => list?.filter(item => item.get('id') !== Number(postId)));

export const clearBetbook = db => db.deleteIn(['betbook', 'lastParam']);

export const clearScrollPosition = db => db.deleteIn(['betbook', 'posts', 'scrollPosition']);

export const fetchPostById = (postType, postId) => db => {
    const post = db.getIn(['betbook', 'posts', 'list'])?.find(item => item.get('id') === Number(postId));
    if (post) {
        const detail = post.toJS();
        dispatch(setPostDetail(detail));
        if (postType === 'ANALYSIS') {
            fetchMinimalTickets([detail.ticket.hash]);
            fetchMarketStatistics([detail.pick.matchId]);
        } else if (postType === 'TICKET') {
            fetchTicket(detail.ticket.hash);
        }
    } else {
        forumClient
            .get(`/v5/betbook/${toUrlPart(postType)}/${postId}`)
            .then(post => {
                dispatch(setPostDetail(post));
                if (postType === 'ANALYSIS') {
                    const betIds = betIdsToLoad(db, [post]);
                    if (betIds.length) {
                        n1LoadBoxByBetIds(betIds).then(bets => {
                            dispatch(mergeLoadedBoxes({ bets }, 'betbook', betIds));
                        });
                    }
                    fetchMinimalTickets([post.ticket.hash]);
                    fetchMarketStatistics([post.pick.matchId]);
                } else if (postType === 'TICKET') {
                    fetchTicket(post.ticket.hash);
                }
            })
            .catch(() => dispatch(setPostDetail({ error: 'NOT_FOUND' })));
    }
};

const setPostDetail = postDetail => db => db.setIn(['betbook', 'detail'], fromJS(postDetail));

export const clearPostDetail = db => db.deleteIn(['betbook', 'detail']);

export const togglePostRatings = (postType, postId, request) => db => {
    if (!db.get('loggedIn')) {
        dispatch(['login.openScreen']);
        return;
    }
    forumClient
        .post(`/v5/betbook/${toUrlPart(postType)}/${postId}/ratings`, `"${request}"`)
        .then(ratingInfo => dispatch(setPostRatings(fromJS(ratingInfo), postId)))
        .catch(e => {
            openErrorMessageModal(e.response);
        });
};

const setPostRatings = (ratings, postId) => db => {
    if (db.getIn(['betbook', 'detail'])) {
        db = db.setIn(['betbook', 'detail', 'ratings'], ratings);
    }
    return db.updateIn(['betbook', 'posts', 'list'], list =>
        list?.map(item => (item.get('id') === Number(postId) ? item.set('ratings', ratings) : item))
    );
};

export const reportPost = (postType, postId, request) => () => {
    forumClient
        .post(`/v5/betbook/${toUrlPart(postType)}/${postId}/reports`, request)
        .then(() =>
            PopupNg.dispatcher.show({
                style: 'success2',
                title: i18n('betbook.report.sucess.title'),
                messages: i18n('betbook.report.success')
            })
        )
        .catch(e => {
            openErrorMessageModal(e.response);
        });
};

const COMMENTS_COUNT = 5;
const getCommentsParams = db => ({
    offset: db.getIn(['betbook', 'comments', 'offset']),
    count: COMMENTS_COUNT
});

export const loadMorePostComments = (subjectType, subjectId) => db => {
    const newDb = db.updateIn(['betbook', 'comments', 'offset'], offset => offset + COMMENTS_COUNT);
    return [newDb, loadMoreComments(subjectType, subjectId, getCommentsParams(newDb))];
};

export const initPostComments = (subjectType, subjectId) => db => {
    dispatch(loadTopic(subjectType, subjectId));
    let newDb = db.setIn(['betbook', 'comments', 'offset'], 0);
    return [newDb, loadMoreComments(subjectType, subjectId, getCommentsParams(newDb))];
};

const loadTopic = (subjectType, subjectId) => () => {
    forumClient
        .get(`/v5/topics/${subjectType}/${subjectId}`)
        .then(topic => {
            dispatch(db => db.setIn(['betbook', 'topic'], fromJS(topic)));
        })
        .catch(() => {
            dispatch(db => db.deleteIn(['betbook', 'topic']));
        });
};

const loadMoreComments = (subjectType, subjectId, params) => () => {
    forumClient
        .get(`/v5/topics/${subjectType}/${subjectId}/comments`, { params })
        .then(comments => {
            dispatch(params.offset === 0 ? setComments(comments) : addComments(comments));
            dispatch(setHasMoreComments(params.count === comments.length));
        })
        .catch(error => {
            if (error?.response?.status === 404) {
                dispatch(setComments([]));
            }
        });
};

const setHasMoreComments = hasMore => db => db.setIn(['betbook', 'comments', 'hasMore'], hasMore);
const setComments = comments => db => {
    const data = normalizeComments(comments, db);
    return db.setIn(['betbook', 'comments', 'messages'], fromJS(data));
};

const addComments = comments => db => {
    const data = normalizeComments(comments, db);
    return db
        .updateIn(['betbook', 'comments', 'messages', 'allIds'], old => old.concat(fromJS(data.get('allIds'))))
        .updateIn(['betbook', 'comments', 'messages', 'byId'], old => old.concat(fromJS(data.get('byId'))));
};

export const toggleCommentRating = (commentId, request) => db => {
    if (!db.get('loggedIn')) {
        dispatch(['login.openScreen']);
    }
    forumClient
        .post(`/v5/comments/${commentId}/ratings`, request)
        .then(rating => dispatch(setCommentRating(rating, commentId)))
        .catch(e => {
            openErrorMessageModal(e.response);
        });
};

export function openErrorMessageModal(response) {
    PopupNg.dispatcher.show({
        style: 'error',
        messages: translateServerError(response?.data?.code)
    });
}

const setCommentRating = (rating, commentId) => db =>
    db.setIn(['betbook', 'comments', 'messages', 'byId', commentId, 'ratings'], fromJS(rating));

export const createComment = (request, subjectType, subjectId, nickname, avatarId) => () => {
    forumClient
        .post(`/v5/topics/${subjectType}/${subjectId}/comments`, request)
        .then(comment =>
            request.parentCommentId
                ? dispatch(setNewComment(fromJS(comment), nickname, avatarId))
                : dispatch(initPostComments(subjectType, subjectId))
        )
        .then(() => dispatch(updateCommentCount))
        .catch(error => {
            if (error.response?.status === 401) {
                const payload = error.response.data;
                dispatch(['ui.showMasterError', 'betbook.comment.message.create.' + payload.code]);

                return Promise.resolve(error.response);
            }
            return Promise.reject(error);
        })
        .catch(e => {
            openErrorMessageModal(e.response);
        });
};

const updateCommentCount = db =>
    db.updateIn(['betbook', 'detail', 'commentCount'], commentCount =>
        (commentCount || 0) + 1 > 99 ? 99 : (commentCount || 0) + 1
    );

const setNewComment = (comment, nickname, avatarId) => db => {
    const ratings = { currentUserRating: null, positive: 0, negative: 0 };
    return db
        .setIn(['betbook', 'comments', 'messages', 'byId', comment.get('id')], comment)
        .setIn(['betbook', 'comments', 'messages', 'byId', comment.get('id'), 'children'], List())
        .setIn(['betbook', 'comments', 'messages', 'byId', comment.get('id'), 'ratings'], fromJS(ratings))
        .setIn(['betbook', 'comments', 'messages', 'byId', comment.get('id'), 'nickname'], nickname)
        .setIn(['betbook', 'comments', 'messages', 'byId', comment.get('id'), 'avatarId'], avatarId)
        .setIn(['betbook', 'comments', 'messages', 'commentedId'], null)
        .setIn(['betbook', 'comments', 'messages', 'newCommentId'], comment.get('id'))
        .updateIn(['betbook', 'comments', 'messages', 'byId', comment.get('parentCommentId'), 'children'], old =>
            List([comment.get('id')]).concat(old)
        );
};

export const setCommentedCommentId = commentId => db =>
    db.setIn(['betbook', 'comments', 'messages', 'commentedId'], commentId);

export const clearComments = db => db.deleteIn(['betbook', 'comments']);

export const setEditedCommentId = commentId => db =>
    db.setIn(['betbook', 'comments', 'messages', 'editedCommentId'], commentId);

export const updateComment = (commentId, text) => db => {
    forumClient
        .put(`/v5/comments/${commentId}`, text)
        .then(() => dispatch(updateCommentText(commentId, text)))
        .catch(status => dispatch(updateCommentStatus(commentId, status.response.data.code)));
    return db.setIn(['betbook', 'comments', 'messages', 'editedCommentId'], null);
};

const updateCommentStatus = (commentId, status) => db =>
    db.setIn(['betbook', 'comments', 'messages', 'byId', commentId, 'status'], status);

const updateCommentText = (commentId, commentText) => db =>
    db
        .setIn(['betbook', 'comments', 'messages', 'byId', commentId, 'text'], commentText)
        .setIn(['betbook', 'comments', 'messages', 'byId', commentId, 'status'], 'EDITED');

export const removeComment = commentId => () => {
    forumClient
        .post(`/v5/comments/${commentId}/actions`, 'DELETE_COMMENT')
        .then(() => dispatch(changeCommentStatus(commentId)))
        .catch(e => {
            openErrorMessageModal(e.response);
        });
};

const changeCommentStatus = commentId => db =>
    db.setIn(['betbook', 'comments', 'messages', 'byId', commentId, 'status'], 'REMOVED_BY_AUTHOR');

export const reportComment = (commentId, text) => () => {
    forumClient
        .post(`/v5/comments/${commentId}/reports`, text)
        .then(() => {
            PopupNg.dispatcher.show({
                style: 'success2',
                title: i18n('betbook.report.sucess.title'),
                messages: i18n('betbook.report.success')
            });
        })
        .catch(e => {
            openErrorMessageModal(e.response);
        });
};

export const setScroll = position => {
    return db => db.setIn(['betbook', 'posts', 'scrollPosition'], position);
};

export const setTopEventsData = data => db => db.setIn(['betbook', 'topEvents', 'data'], fromJS(data));

export const fetchTopEventsData = (dateFilter, sportFilter) => {
    let params = { limit: 20, sportIds: sportFilter };
    dateFilter === TIME_3HOURS ? (params['minutes'] = 180) : (params['date'] = dateFilter);
    stats2Client
        .get('/top-selections', {
            params
        })
        .then(res => {
            dispatch(setTopEventsData(res));
        });
};

export const setTopEventsDateFilter = value => db => db.setIn(['betbook', 'topEvents', 'filter', 'byDate'], value);

export const setTopEventsSportFilter = value => db => db.setIn(['betbook', 'topEvents', 'filter', 'bySport'], value);

export const removeTopEventsDateFilter = () => db => db.removeIn(['betbook', 'topEvents', 'filter', 'byDate']);

export const removeTopEventsSportFilter = () => db => db.removeIn(['betbook', 'topEvents', 'filter', 'bySport']);

export const loadRedactorStatus = db => {
    const loggedIn = db.getIn(['loggedIn']);
    if (loggedIn) {
        const rs = db.getIn(['betbook', 'redactor']);
        if (rs !== true && rs !== false) {
            const nickname = db.getIn(['account', 'userData', 'nickname']);
            forumClient
                .get(`/v5/authors/${nickname}/redactor`)
                .then(redactor => dispatch(db => db.setIn(['betbook', 'redactor'], redactor)))
                .catch(() => dispatch(db => db.setIn(['betbook', 'redactor'], false)));
        }
    }
    return db;
};

export const addAnalysisToChat = analysisId => db => {
    if (!db.get('loggedIn')) {
        dispatch(['login.openScreen']);
    } else {
        navigate(CHAT_NEW_MESSAGE_WITH_PARAMS_ID, { analysisId });
    }
};

export const incrementBetbookPostViewCount = postId => db => {
    const detail = db.getIn(['betbook', 'detail']);
    if (detail) {
        db = db.setIn(['betbook', 'detail', 'viewCount'], (detail.get('viewCount') || 0) + 1);
    }
    return db.updateIn(['betbook', 'posts', 'list'], list =>
        list?.map(p => (p.get('id') === Number(postId) ? p.set('viewCount', (p.get('viewCount') || 0) + 1) : p))
    );
};
